import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Trash2, X } from "lucide-react";

const STAGES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "proposal", label: "Proposal" },
  { value: "negotiation", label: "Negotiation" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

export const BulkActionBar = ({ selectedCount, onStageChange, onDelete, onClear }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <>
      <div
        data-testid="bulk-action-bar"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card border border-white/10 rounded-sm px-6 py-3 flex items-center gap-4 shadow-xl z-50"
      >
        <span className="text-sm font-medium text-foreground whitespace-nowrap" data-testid="bulk-selected-count">
          {selectedCount} lead{selectedCount !== 1 ? "s" : ""} selected
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm border-white/10 text-xs uppercase tracking-wider h-8"
              data-testid="bulk-stage-trigger"
            >
              Change Stage <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="bg-card border-white/10">
            {STAGES.map((s) => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => onStageChange(s.value)}
                data-testid={`bulk-stage-option-${s.value}`}
              >
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDeleteDialogOpen(true)}
          className="rounded-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs uppercase tracking-wider h-8"
          data-testid="bulk-delete-trigger"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
          Delete Selected
        </Button>

        <button
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground transition-colors duration-150 p-1 rounded-sm"
          data-testid="bulk-clear"
          aria-label="Clear selection"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              DELETE {selectedCount} LEAD{selectedCount !== 1 ? "S" : ""}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. {selectedCount} lead{selectedCount !== 1 ? "s" : ""} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-sm border-white/10 uppercase tracking-wider text-xs"
              data-testid="bulk-delete-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDeleteDialogOpen(false);
                onDelete();
              }}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white uppercase tracking-wider text-xs"
              data-testid="bulk-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
